import { Injectable, inject } from "@angular/core";
import {CookieService} from "ngx-cookie-service";
import {compressToBase64, decompressFromBase64} from 'lz-string';
import {BehaviorSubject, Observable} from "rxjs";
import {CourseItem, RankItem, Restaurant, ServiceItem} from "../Services/qdm.service";
import {UserChildren} from "../Services/auth.service";

export interface CartItem {
  id: number;
  name: string;
  quantity: number;
  displayedPrice: number;
  image: string;
  rankId: number;
  rankSort: number;
  gpaoId: string|null;
  isVeganFullCourse: boolean;
  isMeatFullCourse: boolean;
  rankName: string;
  isExtraItem: boolean;
}

export interface CartStructure {
  serviceId: number;
  restaurantId: number;
  childId: number;
  date: string;
  items: CartItem[];
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private cookieService = inject(CookieService);


  public cartSubject = new BehaviorSubject<CartStructure[]>([]);
  public cart$: Observable<CartStructure[]> = this.cartSubject.asObservable();


  constructor() {
      this.init();
  }

  init() {
    const cart = this.readCartContent();
     
    this.cartSubject.next(cart);
  }

  readCartContent(): CartStructure[] {
    const cartString = this.cookieService.get('cart');
    if (!cartString) {
      return [];
    }

    return JSON.parse(decompressFromBase64(cartString));
  }

  saveCartContent(cart: CartStructure[]) {
    const cartString = compressToBase64(JSON.stringify(cart));
    this.cookieService.set('cart', cartString, 365, '/');
  }

  addItemToCart(item: CartItem, serviceId: number, restaurantId: number, childId: number, date: Date) {
    item.quantity = 1;
    const cart = this.readCartContent();

    const existingItem = cart.find(c => c.serviceId === serviceId && c.restaurantId === restaurantId && c.childId === childId && c.date === date.toLocaleDateString("fr-CA"));

    if (existingItem) {
      existingItem.items.push(item);
    } else {
      cart.push({
        "serviceId": serviceId,
        "restaurantId": restaurantId,
        "childId": childId,
        "date": date.toLocaleDateString("fr-CA"),
        items: [item]
      });
    }

    
    this.saveCartContent(cart);
    this.cartSubject.next(cart);
  }

  addMultipleItemsToCart(itemsToAdd: Array<{
    cartItem: CartItem;
    serviceId: number;
    restaurantId: number;
    childId: number;
    date: Date;
  }>, bulkQuantity: number = 1) {
    if (itemsToAdd.length === 0) return;

    const cart = this.readCartContent();

    itemsToAdd.forEach(({ cartItem, serviceId, restaurantId, childId, date }) => {
      cartItem.quantity = bulkQuantity;
      
      const existingItem = cart.find(c => 
        c.serviceId === serviceId && 
        c.restaurantId === restaurantId && 
        c.childId === childId && 
        c.date === date.toLocaleDateString("fr-CA")
      );

      if (existingItem) {
        existingItem.items.push(cartItem);
      } else {
        cart.push({
          serviceId: serviceId,
          restaurantId: restaurantId,
          childId: childId,
          date: date.toLocaleDateString("fr-CA"),
          items: [cartItem]
        });
      }
    });

    // Une seule sauvegarde et émission pour tous les items
    this.saveCartContent(cart);
    this.cartSubject.next(cart);
  }

  quantityPresentInCart(courseId: number, serviceId: number, restaurantId: number, childId: number, date: Date): number {
    const cart = this.readCartContent();

    const existingItem = cart.find(c => c.serviceId === serviceId && c.restaurantId === restaurantId && c.childId === childId && c.date === date.toLocaleDateString("fr-CA"));

    if (!existingItem) {
      return 0;
    }

    return existingItem.items.filter(item => item.id === courseId).reduce((acc, item) => acc + item.quantity, 0);
  }

  removeItemFromCart(courseId: number, serviceId: number, restaurantId: number, childId: number, date: Date) {
    const cart = this.readCartContent();

    const existingItem = cart.find(c => c.serviceId === serviceId && c.restaurantId === restaurantId && c.childId === childId && c.date === date.toLocaleDateString("fr-CA"));

    if (!existingItem) {
      return;
    }

    existingItem.items = existingItem.items.filter(item => item.id !== courseId);

    this.saveCartContent(cart);
    this.cartSubject.next(cart);
  }

  computeCartTotal(serviceId: number, restaurantId: number, childId: number, date: Date): number {
    const cart = this.readCartContent();

    const existingItem = cart.find(c => c.serviceId === serviceId && c.restaurantId === restaurantId && c.childId === childId && c.date === date.toLocaleDateString("fr-CA"));

    if (!existingItem) {
      return 0;
    }

    return existingItem.items.reduce((acc, item) => acc + item.quantity * item.displayedPrice, 0);
  }

  setItemQuantity(courseId: number, quantity: number, serviceId: number, restaurantId: number, childId: number, date: Date) {
    const cart = this.readCartContent();

    const existingItem = cart.find(c => c.serviceId === serviceId && c.restaurantId === restaurantId && c.childId === childId && c.date === date.toLocaleDateString("fr-CA"));

    if (!existingItem) {
      return;
    }

    const item = existingItem.items.find(item => item.id === courseId);

    if (!item) {
      return;
    }

    item.quantity = quantity;

    this.saveCartContent(cart);
    this.cartSubject.next(cart);
  }


  clearCart(selectedDate: Date, selectedChild: UserChildren | undefined, currentService: ServiceItem | undefined, restaurant: Restaurant | undefined) {
    const cart = this.readCartContent();
    const dateStr = selectedDate.toLocaleDateString("fr-CA");

    // remove from cart everything that matches date, child service and restaurant
    const updatedCart = cart.filter(c => {
      if (c.date === dateStr) {
        if (selectedChild && c.childId !== selectedChild.id) {
          return true;
        }

        if (currentService && c.serviceId !== currentService.id) {
          return true;
        }

        return !!(restaurant && c.restaurantId !== restaurant.id);
      }

      return true;
    });

    this.saveCartContent(updatedCart);
    this.cartSubject.next(updatedCart);
  }

  getCartFor(serviceId: number, restaurantId: number, childId: number, date: Date): CartItem[] {
    const cart = this.readCartContent();

    const existingItem = cart.find(c => c.serviceId === serviceId && c.restaurantId === restaurantId && c.childId === childId && c.date === date.formatDate('forApi'));

    if (!existingItem) {
      return [];
    }

    return existingItem.items.sort((a, b) => a.rankId - b.rankId);
  }

  getFullMeatCoursesCount(serviceId: number, restaurantId: number, childId: number, date: Date): number {
    const cart = this.readCartContent();

    const existingItem = cart.find(c => c.serviceId === serviceId && c.restaurantId === restaurantId && c.childId === childId && c.date === date.formatDate('forApi'));

    if (!existingItem) {
      return 0;
    }

    return existingItem.items.filter(item => item.isMeatFullCourse).reduce((acc, item) => acc + item.quantity, 0);
  }

  getFullVeganCoursesCount(serviceId: number, restaurantId: number, childId: number, date: Date): number {
    const cart = this.readCartContent();

    const existingItem = cart.find(c => c.serviceId === serviceId && c.restaurantId === restaurantId && c.childId === childId && c.date === date.formatDate('forApi'));

    if (!existingItem) {
      return 0;
    }

    return existingItem.items.filter(item => item.isVeganFullCourse).reduce((acc, item) => acc + item.quantity, 0);
  }


  deleteAllCarts() {
    this.cookieService.delete('cart', '/');
    this.cartSubject.next([]);
  }
  toCartItem(course: CourseItem, rank: RankItem, quantity: number = 0): CartItem {
    return {
      id: course.id,
      name: course.name,
      quantity: 0,
      displayedPrice: course.price || 0,
      image: course.image,
      rankId: rank.family_id || rank.id,
      gpaoId: course.gpaoId,
      isMeatFullCourse: course.isMeatFullCourse,
      isVeganFullCourse: course.isVeganFullCourse,
      rankName: rank.name,
      rankSort: parseInt(rank.rank || '0'),
      isExtraItem: course.is_extra
    }
  }
}
